import { useTranslation } from 'react-i18next'
import { isImeiHubPaidService, type ImeiHubServiceId } from '@redevicer/shared'
import { IMEI_HUB_SERVICE_CHECK_KEYS } from './imeiHubServiceMeta'
import ImeiVerifyReport from './ImeiVerifyReport'
import ImeiHubServiceTitle from './ImeiHubServiceTitle'
import ImeiHubServicePriceBadge from './ImeiHubServicePriceBadge'
import { imeiHubPreviewSample } from '../utils/imeiHubPreviewData'

const PAID_POINT_KEYS: Partial<Record<ImeiHubServiceId, readonly string[]>> = {
  gsx: [
    'vc.imeiHub.services.gsx.paidPoint1',
    'vc.imeiHub.services.gsx.paidPoint2',
    'vc.imeiHub.services.gsx.paidPoint3',
  ],
  tmobile: [
    'vc.imeiHub.services.tmobile.paidPoint1',
    'vc.imeiHub.services.tmobile.paidPoint2',
    'vc.imeiHub.services.tmobile.paidPoint3',
  ],
}

type Props = {
  service: ImeiHubServiceId
  priceEurCents?: number | null
}

/** Service explainer: plain checklist + sample report (no charts or fake result rows). */
export default function ImeiHubPanelDocs({ service, priceEurCents }: Props) {
  const { t } = useTranslation()
  const checks = IMEI_HUB_SERVICE_CHECK_KEYS[service] ?? []
  const preview = imeiHubPreviewSample(service)
  const paid = typeof priceEurCents === 'number' || isImeiHubPaidService(service)
  const paidPoints = PAID_POINT_KEYS[service]

  return (
    <div className="vc-imei-hub-docs" aria-hidden="true">
      <div className="vc-imei-hub-docs__layout">
          <div className="vc-imei-hub-docs__main">
            {paid ? (
              <div className="vc-imei-hub-docs__premium">
                <div className="vc-imei-hub-docs__premium-top">
                  <span className="vc-imei-hub-docs__premium-kicker">{t('vc.imeiHub.pricing.premiumKicker')}</span>
                  <ImeiHubServicePriceBadge service={service} priceEurCents={priceEurCents} variant="panel" />
                </div>
                <h4 className="vc-imei-hub-docs__premium-title">
                  <ImeiHubServiceTitle
                    service={service}
                    title={t(`vc.imeiHub.services.${service}.title`)}
                    variant="docs"
                  />
                </h4>
                <p className="vc-imei-hub-docs__premium-lead">
                  {t(`vc.imeiHub.services.${service}.paidIntro`, {
                    defaultValue: t(`vc.imeiHub.services.${service}.detail`),
                  })}
                </p>
                {paidPoints ? (
                  <ul className="vc-imei-hub-docs__premium-points">
                    {paidPoints.map((key) => (
                      <li key={key}>{t(key)}</li>
                    ))}
                  </ul>
                ) : null}
                <p className="vc-imei-hub-docs__premium-foot">{t('vc.imeiHub.pricing.premiumCheckoutNote')}</p>
              </div>
            ) : null}

            <header className={`vc-imei-hub-docs__head${paid ? ' vc-imei-hub-docs__head--after-premium' : ''}`}>
              {!paid ? (
                <h4 className="vc-imei-hub-docs__title">
                  <ImeiHubServiceTitle
                    service={service}
                    title={t(`vc.imeiHub.services.${service}.title`)}
                    variant="docs"
                  />
                </h4>
              ) : (
                <h5 className="vc-imei-hub-docs__subtitle">{t('vc.imeiHub.docs.includedChecksTitle')}</h5>
              )}
              <p className="vc-imei-hub-docs__lead">{t(`vc.imeiHub.services.${service}.detail`)}</p>
            </header>

            <div className="vc-imei-hub-docs__checks-callout">
              <h5 className="vc-imei-hub-docs__steps-title">{t('vc.imeiHub.docs.stepsTitle')}</h5>
              <ul className="vc-imei-hub-docs__simple-list">
                {checks.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="vc-imei-hub-docs__aside vc-imei-hub-docs__aside--preview">
            <p className="vc-imei-hub-docs__preview-note">{t('vc.imeiHub.docs.previewNote')}</p>
            <div className="vc-imei-hub-docs__report-preview">
              <ImeiVerifyReport
                preview
                variant="inline"
                service={service}
                data={preview.data}
                imeiMasked={preview.imeiMasked}
                checkedAt={preview.checkedAt}
                token="preview"
              />
            </div>
          </aside>
      </div>
    </div>
  )
}
